/**
 * Editor-side geometry helpers: cursor selection and screen-facing rect
 * corners. The pure model geometry that used to live here (edgeLeg,
 * primaryEdgeLineFrame, isHorizontal, edgeWorld, localCoords,
 * lineSegPositionRange, lineSegWidthRange, edgeItemWithDefaults) moved into
 * the @kaiizen/kai-floor-plan package (Phase 2, state refactor) — re-exported
 * below so editor consumers keep one import path.
 */
import type { CornerName, Point } from '@kaiizen/kai-floor-plan';

export {
  edgeItemWithDefaults,
  edgeLeg,
  edgeWorld,
  isHorizontal,
  lineSegPositionRange,
  lineSegWidthRange,
  localCoords,
  primaryEdgeLineFrame,
} from '@kaiizen/kai-floor-plan';

/** Plain double-arrow resize cursor for a vector's dominant axis. */
function axisCursor(v: Point): string {
  const ax = Math.abs(v.x), ay = Math.abs(v.y);
  if (ax > ay * 2) return 'ew-resize';
  if (ay > ax * 2) return 'ns-resize';
  return v.x * v.y > 0 ? 'nwse-resize' : 'nesw-resize';
}

/**
 * Cursor for dragging a line/edge perpendicular to itself (moving a wall or
 * divider). `perp` is the line's normal.
 *
 * We deliberately use the plain ns-/ew-resize arrows here (NOT the
 * bar-between-arrows row-/col-resize variants). row-/col-resize have no native
 * macOS cursor, so Chrome renders them as its own bitmap and re-rasterizes them
 * as the pointer crosses device-pixel boundaries on a HiDPI/Retina display —
 * which the user sees as the cursor flickering between the resize arrow and the
 * default pointer. ns-/ew-resize map to native macOS cursors and never flicker.
 * The perpendicular vs. along distinction still reads clearly because, for any
 * given edge, the two gestures point along perpendicular axes (ns vs ew).
 */
export function perpCursor(perp: Point): string {
  return axisCursor(perp);
}

/**
 * Cursor for sliding an object ALONG a line — e.g. an edge-segment moving down
 * the length of its parent edge. The double-arrows point along the line (the
 * perpendicular axis to the line's normal). `perp` is the line's normal.
 */
export function alongCursor(perp: Point): string {
  return axisCursor({ x: -perp.y, y: perp.x });
}

/** Cursor for a chamfer handle, by the corner's signs: the diagonal resize arrow along the cut. */
export const diagCursor = (sx: number, sy: number): string => sx === sy ? 'nwse-resize' : 'nesw-resize';

export function rectCornerPoint(r: { x: number; y: number; width: number; length: number }, which: CornerName): Point {
  return {
    x: which === 'backRightCorner' || which === 'frontRightCorner' ? r.x + r.width : r.x,
    y: which === 'frontLeftCorner' || which === 'frontRightCorner' ? r.y + r.length : r.y,
  };
}
